// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Warble — the three modes (principle #14). The RULE SET is the axis that changes the
// game, not a dialled number:
//   · Glide  — a smooth, forgiving ribbon that wanders; TRACK a moving line, score by
//              time on it. The calm showcase + the daily. No way to die.
//   · Steps  — the ribbon is a staircase of DISCRETE held notes with rests between;
//              LEAP your pitch to each level and HOLD it steady. A settle-and-hold
//              game, structurally different from tracking a moving line.
//   · Thread — a NARROW ribbon strung through walls with gaps; miss a wall's gap (or
//              drift too far) and the run ENDS. One life — a sudden-death gauntlet.
//
// The host's chosen mode travels FROZEN inside the round start (rematch roundOpts);
// guests render state().hostOpts. An id off the wire is always modeOf()-validated so an
// unknown value falls back to Glide rather than reaching the generator as undefined
// (guarded with Object.hasOwn — a wire key must never resolve a prototype member).

export type ModeId = 'glide' | 'steps' | 'thread';
export type ModeKind = 'glide' | 'steps' | 'thread';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  readonly kind: ModeKind;
  /** Course length in ms (Thread: the survive-to-here cap; it can end earlier on death). */
  readonly durMs: number;
  /** On-line half-width in height units (0..1). Smaller = stricter. */
  readonly tol: number;
  /** Score gained per ms at perfect on-ribbon tracking (before the combo multiplier). */
  readonly rate: number;
  /** Glide/Thread ribbon: average spacing between wander waypoints (ms) and its amplitude. */
  readonly waypointMs: number;
  readonly amp: number;
  /** Steps: platform hold length, rest gap length, how many platforms, how many levels. */
  readonly platformMs: number;
  readonly gapMs: number;
  readonly steps: number;
  readonly levels: number;
  /** Thread: wall spacing (ms), the gap's half-width, and one-life sudden death. */
  readonly gateMs: number;
  readonly gateGap: number;
  readonly oneLife: boolean;
}

// ── shared scoring constants (the same across modes; only the RULES above differ) ──
export const COMBO_MAX = 5; // the multiplier caps here
export const COMBO_STEP_MS = 1400; // this much continuous on-ribbon time raises the multiplier
export const GATE_BONUS = 60; // Thread: banked (× combo) for threading a wall's gap
export const NOTE_HIT_FRAC = 0.55; // Steps: a platform counts as "hit" if on-line this fraction of it
export const COUNT_IN_STEPS = ['3', '2', '1', 'Sing!'];

export const MODES: Record<ModeId, Mode> = {
  glide: {
    id: 'glide',
    name: 'Glide',
    blurb: 'A gentle ribbon of light that wanders. Ride it with your voice — stay on the line and your combo climbs.',
    kind: 'glide',
    durMs: 42_000,
    tol: 0.14,
    rate: 0.04,
    waypointMs: 2600,
    amp: 0.36,
    platformMs: 0,
    gapMs: 0,
    steps: 0,
    levels: 0,
    gateMs: 0,
    gateGap: 0,
    oneLife: false,
  },
  steps: {
    id: 'steps',
    name: 'Steps',
    blurb: 'A staircase of held notes. Leap your pitch to each rung and hold it steady — a rest between each.',
    kind: 'steps',
    durMs: 0, // derived from steps × (platformMs + gapMs) — see courseDurMs()
    tol: 0.085,
    rate: 0.05,
    waypointMs: 0,
    amp: 0,
    platformMs: 1500,
    gapMs: 720,
    steps: 18,
    levels: 5,
    gateMs: 0,
    gateGap: 0,
    oneLife: false,
  },
  thread: {
    id: 'thread',
    name: 'Thread',
    blurb: 'A narrow ribbon strung through walls. Thread every gap — one miss and the run is over.',
    kind: 'thread',
    durMs: 55_000,
    tol: 0.06,
    rate: 0.05,
    waypointMs: 1900,
    amp: 0.3,
    platformMs: 0,
    gapMs: 0,
    steps: 0,
    levels: 0,
    gateMs: 2100,
    gateGap: 0.1,
    oneLife: true,
  },
};

export const MODE_IDS: ModeId[] = ['glide', 'steps', 'thread'];

export const DEFAULT_MODE: Mode = MODES.glide;

/** The true course length for a mode (Steps derives it from its platform schedule). */
export function courseDurMs(mode: Mode): number {
  if (mode.kind === 'steps') return mode.steps * (mode.platformMs + mode.gapMs);
  return mode.durMs;
}

/** Resolve a (possibly untrusted, off-the-wire) id to a Mode; unknown → Glide. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) {
    return MODES[id as ModeId];
  }
  return DEFAULT_MODE;
}
